import { DiffLineKind } from '../../../../api/agent-miner-client/agent-miner-client.type';
import type {
  PullRequestDiffHunk,
  PullRequestDiffLine,
  PullRequestFile,
  SessionPullRequest,
} from '../../../../api/agent-miner-client/agent-miner-client.type';
import type {
  FileSurvivalSegments,
  Lineage,
  LineageEdit,
  LineageFile,
  LineAttributionDisplayKind,
} from './pr-survival.helpers.type';

/**
 * Splits a PR file's added lines into the bars the files card draws. Counts are
 * clamped so legacy rows (where survived can exceed authored) never produce
 * negative segments. Lines merged by agents outside the viewed session's
 * subtree get their own bucket so `human` no longer absorbs them.
 * @param {PullRequestFile} file - The attributed PR file.
 * @returns {FileSurvivalSegments} Kept, not merged, other-agent, and human line counts.
 */
const fileSurvivalSegments = (file: PullRequestFile): FileSurvivalSegments => {
  const agentOther = Math.max(0, file.agent_other ?? 0);
  return {
    kept: file.survived,
    notMerged: Math.max(0, file.authored - file.survived),
    agentOther,
    human: Math.max(0, file.additions - file.survived - agentOther),
  };
};

/**
 * Percentage of the agent's unique lines in a file that reached the merge.
 * @param {PullRequestFile} file - The attributed PR file.
 * @returns {number | null} A rounded percentage, or null when the agent authored nothing.
 */
const fileKeptPct = (file: PullRequestFile): number | null =>
  file.authored > 0 ? Math.round(Math.min(100, (file.survived / file.authored) * 100)) : null;

/**
 * Classifies a diff line for styling and tagging.
 * @param {PullRequestDiffLine} line - A line of the merged diff.
 * @param {string} currentSessionId - The session whose detail page is open.
 * @returns {LineAttributionDisplayKind} `none` for context/deleted lines, `human` for
 *   unattributed additions, otherwise which agent bucket the line falls in.
 */
const lineAttributionKind = (line: PullRequestDiffLine, currentSessionId: string): LineAttributionDisplayKind => {
  if (line.kind !== DiffLineKind.Add) return 'none';
  if (!line.attribution) return 'human';
  if (line.attribution.match_kind === 'ambiguous') return 'agent-ambiguous';
  return line.attribution.session_id === currentSessionId ? 'agent-current' : 'agent-other';
};

/**
 * Roving-tabindex keyboard handling for the file tab strip.
 * @param {number} current - Index of the focused tab.
 * @param {string} key - The `KeyboardEvent.key` pressed.
 * @param {number} count - Number of tabs.
 * @returns {number | null} The index to move to, or null when the key is not a navigation key.
 */
const nextTabIndex = (current: number, key: string, count: number): number | null => {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
};

/**
 * Picks the file to open first: the first one the agent authored lines in.
 * @param {PullRequestFile[]} files - The PR's attributed files.
 * @returns {string | null} A file id, or null when there are no files.
 */
const defaultFileId = (files: PullRequestFile[]): string | null =>
  files.find((file) => file.authored > 0)?.change_request_file_id ?? files[0]?.change_request_file_id ?? null;

/**
 * Splits hunks into an initially visible set and a hidden remainder. Hunks are
 * kept whole, and a new hunk is only started while the running total is still
 * under the budget, so the first hunk always renders.
 * @param {PullRequestDiffHunk[]} hunks - All hunks of the file.
 * @param {number} budget - Approximate number of lines to render up front.
 * @returns {{ visible: PullRequestDiffHunk[]; hidden: PullRequestDiffHunk[] }} The split.
 */
const hunkLineBudget = (
  hunks: PullRequestDiffHunk[],
  budget: number,
): { visible: PullRequestDiffHunk[]; hidden: PullRequestDiffHunk[] } => {
  const visible: PullRequestDiffHunk[] = [];
  const hidden: PullRequestDiffHunk[] = [];
  let total = 0;
  for (const hunk of hunks) {
    if (visible.length === 0 || total < budget) {
      visible.push(hunk);
      total += hunk.lines.length;
    } else {
      hidden.push(hunk);
    }
  }
  return { visible, hidden };
};

type FileGroup = {
  /** Directory path without a trailing slash; empty string for root files. */
  directory: string;
  files: PullRequestFile[];
};

/**
 * The directory portion of a path, without a trailing slash.
 * @param {string | null} filename - A file path, or null.
 * @returns {string} The directory, or an empty string for a root file or null path.
 */
const directoryOf = (filename: string | null): string => {
  const path = filename ?? '';
  const slash = path.lastIndexOf('/');
  return slash === -1 ? '' : path.slice(0, slash);
};

/**
 * The final path segment (file name) of a path.
 * @param {string | null} filename - A file path, or null.
 * @returns {string} The base name, or an empty string for a null path.
 */
const baseNameOf = (filename: string | null): string => (filename ?? '').split('/').pop() ?? '';

/**
 * Groups PR files by directory for the file tree: root files first, then
 * directories alphabetically, files within a group sorted by name.
 * @param {PullRequestFile[]} files - The PR's attributed files.
 * @returns {FileGroup[]} Ordered directory groups.
 */
const groupFilesByDirectory = (files: PullRequestFile[]): FileGroup[] => {
  const groups = new Map<string, PullRequestFile[]>();
  for (const file of files) {
    const directory = directoryOf(file.filename);
    groups.set(directory, [...(groups.get(directory) ?? []), file]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    .map(([directory, groupFiles]) => ({
      directory,
      files: [...groupFiles].sort((a, b) => baseNameOf(a.filename).localeCompare(baseNameOf(b.filename))),
    }));
};

type ContributorRow = {
  session_id: string;
  title: string | null;
  user: string | null;
  source: PullRequestSessionSource;
  is_current: boolean;
  lines: number;
  /** Share of the PR's agent lines, 0..100. */
  share: number;
  /** Distinct files this session's edits landed in; null without lineage data. */
  files: number | null;
  /** Distinct edits this session contributed; null without lineage data. */
  edits: number | null;
};

type SourceSplitSegment = {
  source: PullRequestSessionSource;
  lines: number;
  /** Share of the PR's agent lines, 0..100. */
  pct: number;
};

type SessionFileMatrix = {
  sessions: Array<{ session_id: string; title: string | null; is_current: boolean }>;
  files: Array<{ change_request_file_id: string; filename: string | null }>;
  /** Keyed by `${session_id}|${change_request_file_id}`; null when lines are unknown. */
  cells: Map<string, number | null>;
};

type ContributionCounts = {
  sessions: number;
  edits: number;
  filesWithAgentLines: number;
};

type PullRequestSessionSource = SessionPullRequest['contributing_sessions'][number]['source'];

/**
 * Composite key for the session × file matrix cell map.
 * @param {string} sessionId - The contributing session id.
 * @param {string} fileId - The change-request file id.
 * @returns {string} The `${sessionId}|${fileId}` cell key.
 */
const matrixKey = (sessionId: string, fileId: string): string => `${sessionId}|${fileId}`;

/**
 * One table row per contributing session, enriched with files touched and edit
 * counts from the per-file lineage, sorted by lines descending (current session
 * first on ties).
 * @param {SessionPullRequest} pr - The pull request payload.
 * @returns {ContributorRow[]} The rows.
 */
const contributorRows = (pr: SessionPullRequest): ContributorRow[] => {
  const perSession = new Map<string, { files: Set<string>; edits: Set<string> }>();
  let hasLineage = false;
  for (const file of pr.files) {
    for (const edit of file.contributing_edits ?? []) {
      hasLineage = true;
      const entry = perSession.get(edit.session_id) ?? { files: new Set<string>(), edits: new Set<string>() };
      entry.files.add(file.change_request_file_id);
      entry.edits.add(edit.file_edit_id);
      perSession.set(edit.session_id, entry);
    }
  }
  const total = pr.contributing_sessions.reduce((sum, session) => sum + session.lines, 0);
  return pr.contributing_sessions
    .map((session) => {
      const entry = perSession.get(session.session_id);
      return {
        session_id: session.session_id,
        title: session.title,
        user: session.user,
        source: session.source,
        is_current: session.is_current,
        lines: session.lines,
        share: total > 0 ? Math.round((session.lines / total) * 100) : 0,
        files: hasLineage ? (entry?.files.size ?? 0) : null,
        edits: hasLineage ? (entry?.edits.size ?? 0) : null,
      };
    })
    .sort((a, b) => b.lines - a.lines || Number(b.is_current) - Number(a.is_current));
};

/**
 * Agent lines grouped by agent source, largest first.
 * @param {SessionPullRequest} pr - The pull request payload.
 * @returns {SourceSplitSegment[]} One segment per source.
 */
const sourceSplit = (pr: SessionPullRequest): SourceSplitSegment[] => {
  const bySource = new Map<PullRequestSessionSource, number>();
  for (const session of pr.contributing_sessions) {
    bySource.set(session.source, (bySource.get(session.source) ?? 0) + session.lines);
  }
  const total = Array.from(bySource.values()).reduce((sum, lines) => sum + lines, 0);
  return Array.from(bySource.entries())
    .map(([source, lines]) => ({ source, lines, pct: total > 0 ? (lines / total) * 100 : 0 }))
    .sort((a, b) => b.lines - a.lines);
};

/**
 * Session × file grid of matched lines, from the per-file lineage.
 * @param {SessionPullRequest} pr - The pull request payload.
 * @returns {SessionFileMatrix} Row and column headers plus a cell map.
 */
const sessionFileMatrix = (pr: SessionPullRequest): SessionFileMatrix => {
  const cells = new Map<string, number | null>();
  for (const file of pr.files) {
    for (const edit of file.contributing_edits ?? []) {
      const key = matrixKey(edit.session_id, file.change_request_file_id);
      const current = cells.get(key);
      if (edit.lines_matched === null) {
        if (current === undefined) cells.set(key, null);
      } else {
        cells.set(key, (current ?? 0) + edit.lines_matched);
      }
    }
  }
  return {
    sessions: pr.contributing_sessions.map((session) => ({
      session_id: session.session_id,
      title: session.title,
      is_current: session.is_current,
    })),
    files: pr.files.map((file) => ({ change_request_file_id: file.change_request_file_id, filename: file.filename })),
    cells,
  };
};

/**
 * Headline counts for the Contributors view.
 * @param {SessionPullRequest} pr - The pull request payload.
 * @returns {ContributionCounts} Sessions, distinct edits, and files carrying agent lines.
 */
const contributionCounts = (pr: SessionPullRequest): ContributionCounts => {
  const edits = new Set<string>();
  for (const file of pr.files) {
    for (const edit of file.contributing_edits ?? []) edits.add(edit.file_edit_id);
  }
  // Count only files that actually carry agent lines — `pr.files` can include
  // human-only entries (authored 0, no other-agent lines), which must not count.
  const filesWithAgentLines = pr.files.filter((file) => file.authored > 0 || (file.agent_other ?? 0) > 0).length;
  return { sessions: pr.contributing_sessions.length, edits: edits.size, filesWithAgentLines };
};

/**
 * Sums a list of counts, preserving an all-unknown state as null: null values
 * are treated as zero, but a list with no known value returns null rather than 0.
 * @param {Array<number | null>} values - Counts, some possibly unknown (null).
 * @returns {number | null} The total, or null when every value is null.
 */
const sumOrNull = (values: Array<number | null>): number | null =>
  values.some((value) => value !== null) ? values.reduce<number>((sum, value) => sum + (value ?? 0), 0) : null;

/**
 * Builds the edit → file → PR lineage graph from the PR payload's per-file
 * `contributing_edits`, deduplicating edits that touched several files.
 * @param {SessionPullRequest} pr - The pull request payload.
 * @param {string} currentSessionId - The session whose detail page is open.
 * @returns {Lineage} Nodes for both columns plus the adjacency maps between them.
 */
const buildLineage = (pr: SessionPullRequest, currentSessionId: string): Lineage => {
  const editsById = new Map<string, LineageEdit>();
  const editLineTotals = new Map<string, Array<number | null>>();
  const editToFiles = new Map<string, string[]>();
  const fileToEdits = new Map<string, string[]>();
  const files: LineageFile[] = [];
  let available = false;

  for (const file of pr.files) {
    const contributingEdits = file.contributing_edits;
    if (!contributingEdits) continue;
    available = true;
    const editIds: string[] = [];
    for (const edit of contributingEdits) {
      editIds.push(edit.file_edit_id);
      if (!editsById.has(edit.file_edit_id)) {
        editsById.set(edit.file_edit_id, {
          file_edit_id: edit.file_edit_id,
          turn_number: edit.turn_number,
          session_id: edit.session_id,
          session_title: edit.session_title,
          is_current: edit.session_id === currentSessionId,
          lines_matched: null,
        });
      }
      editLineTotals.set(edit.file_edit_id, [...(editLineTotals.get(edit.file_edit_id) ?? []), edit.lines_matched]);
      editToFiles.set(edit.file_edit_id, [...(editToFiles.get(edit.file_edit_id) ?? []), file.change_request_file_id]);
    }
    fileToEdits.set(file.change_request_file_id, editIds);
    files.push({
      change_request_file_id: file.change_request_file_id,
      filename: file.filename,
      lines_matched: sumOrNull(contributingEdits.map((edit) => edit.lines_matched)),
      edit_ids: editIds,
    });
  }

  const edits = Array.from(editsById.values()).map((edit) => ({
    ...edit,
    lines_matched: sumOrNull(editLineTotals.get(edit.file_edit_id) ?? []),
  }));
  edits.sort((a, b) => Number(b.is_current) - Number(a.is_current) || (a.turn_number ?? 0) - (b.turn_number ?? 0));

  return { edits, files, editToFiles, fileToEdits, available };
};

export {
  baseNameOf,
  buildLineage,
  contributionCounts,
  contributorRows,
  defaultFileId,
  fileKeptPct,
  fileSurvivalSegments,
  groupFilesByDirectory,
  hunkLineBudget,
  lineAttributionKind,
  matrixKey,
  nextTabIndex,
  sessionFileMatrix,
  sourceSplit,
};
export type {
  ContributionCounts,
  ContributorRow,
  FileGroup,
  FileSurvivalSegments,
  Lineage,
  LineageEdit,
  LineageFile,
  LineAttributionDisplayKind,
  SessionFileMatrix,
  SourceSplitSegment,
};
